import {
    createFeatureSelector,
    createSelector
} from '@ngrx/store';
import {
    DatiqUserTierOne,
    DatiqUserTiers,
    DatiqUserTierThree,
    DatiqUserTierTwo,
    emptyDatiqUserModalInfo,
    IDatiqUser,
} from '../models';
import {
    DATIQ_USER_FEATURE_KEY,
    datiqUserAdapter,
    DatiqUserPartialState,
    State,
} from './datiq-user.reducer';
import {
    all,
    whereEq
} from 'ramda';

// Lookup the 'DatiqUser' feature state managed by NgRx
export const getDatiqUserState = createFeatureSelector<
    DatiqUserPartialState,
    State
>(DATIQ_USER_FEATURE_KEY);

const { selectAll, selectEntities } = datiqUserAdapter.getSelectors();

export const getDatiqUserLoaded = createSelector(
    getDatiqUserState,
    (state: State) => state.loaded
);

export const getDatiqUserError = createSelector(
    getDatiqUserState,
    (state: State) => state.error
);

export const getAllDatiqUser = createSelector(
    getDatiqUserState,
    (state: State) => selectAll(state)
);

export const getDatiqUserEntities = createSelector(
    getDatiqUserState,
    (state: State) => (!!state ? selectEntities(state) : {})
);

export const getSelectedId = createSelector(getDatiqUserState, (state: State) =>
    !!state ? state.selectedId : ''
);

export const getSelected = createSelector(
    getDatiqUserEntities,
    getSelectedId,
    (entities, selectedId) => selectedId && entities[selectedId]
);
export const getSelectedLoaded = createSelector(
    getSelected,
    (user: IDatiqUser): boolean => !!user?.loaded
);
export const getSelectedLoggedIn = createSelector(
    getSelected,
    (user: IDatiqUser): boolean => !!user?.loggedIn
);

export const getUserLoggedInTier = () =>
    createSelector(getSelected, (user: IDatiqUser): DatiqUserTiers => {
        const tier3: boolean = hasUserPermissions(user, {
            permissions: DatiqUserTierThree,
        });
        const tier2: boolean = hasUserPermissions(user, {
            permissions: DatiqUserTierTwo,
        });
        const tier1: boolean = hasUserPermissions(user, {
            permissions: DatiqUserTierOne,
        });

        return !!tier3
            ? DatiqUserTiers.TierThree
            : !!tier2
            ? DatiqUserTiers.TierTwo
            : !!tier1
            ? DatiqUserTiers.TierOne
            : DatiqUserTiers.NoTier;
    });
export const isLoggedInUserTier = (tier: DatiqUserTiers) =>
    createSelector(
        getUserLoggedInTier(),
        (userTier: DatiqUserTiers): boolean => {
            return tier === userTier;
        }
    );

export const getUiCache = ({ id }: { id: string }) =>
    createSelector(
        getSelected,
        (user: IDatiqUser) => user?.uiStates?.[id] ?? {}
    );

export const hasUserPermissions = (
    user: IDatiqUser,
    props: { permissions: string[] }
): boolean => {
    return (
        !props?.permissions ||
        props?.permissions?.reduce(
            (hasAllPermissions: boolean, currentPermission: string): boolean =>
                hasAllPermissions &&
                user?.authorizations?.permissions.includes(currentPermission),
            true
        )
    );
};

export const hasPermissions = ({ permissions }: { permissions: string[] }) =>
    createSelector(getSelected, (user: IDatiqUser) =>
        hasUserPermissions(user, { permissions })
    );

export const getModalState = (modalId: string) =>
    createSelector(getSelected, (user: IDatiqUser) =>
        !!user?.modals && !!user?.modals[modalId]
            ? user?.modals[modalId]
            : { ...emptyDatiqUserModalInfo }
    );

export const hasUserCapability =
    (user: IDatiqUser) =>
    (capability: string): boolean => 
        {
            return whereEq(
                { [capability]: true },
                user.capabilities
            )
        };
export const hasUserCapabilities =
    (user: IDatiqUser) =>
    (capabilities: string[]) =>
        all(hasUserCapability(user), capabilities);

export const hasCapabilities = (capabilities: string[]) =>
    createSelector(getSelected, (user: IDatiqUser) => hasUserCapabilities(user)(capabilities));
